// useLocalizedCopy.ts
import { useState, useEffect } from "react";
import { Language } from "../components/LanguagePickerMenuItem";
import { getCurrentLanguage } from "../core/_axios";

/** ------------------------------------------------------------------ */
/** 1️⃣  Strongly-typed structure for all the copy in the form.        */
/** ------------------------------------------------------------------ */
type CreateServiceFormCopy = {
    photoLabel: string;
    useSampleAvatarLink: string;
    selectSampleAvatarLabel: string;

    fullNameLabel: string;
    fullNamePlaceholder: string;

    preferredNameLabel: string;
    preferredNamePlaceholder: string;

    birthDateLabel: string;
    deathDateLabel: string;

    hidePreplanningCheckboxLabel: string;
    defaultPreplanningCheckboxLabel: string;
    preplanningLabel: string;
    preplanningLabelPlaceholder: string;

    mobileLabelHeaderLabel: string;
    mobileLabelHeaderPlaceholder: string;

    mobileQuestionLabel: string;
    mobileQuestionPlaceholder: string;

    viewAdvancedOptionsLabel: string;

    createButton: string;
    updateButton: string;

    savingCreateMsg: string;
    savingUpdateMsg: string;
};

type LocalizedCopy = {
    // Common UI elements
    Common: {
        buttons: {
            save: string;
            cancel: string;
            delete: string;
            edit: string;
            close: string;
            confirm: string;
            back: string;
            next: string;
            submit: string;
            add: string;
            remove: string;
            view: string;
            download: string;
            upload: string;
            print: string;
            share: string;
            export: string;
            import: string;
            refresh: string;
            search: string;
            filter: string;
            clear: string;
            reset: string;
            continue: string;
            finish: string;
            create: string;
            update: string;
            ok: string;
            yes: string;
            no: string;
        };
        labels: {
            loading: string;
            saving: string;
            deleting: string;
            processing: string;
            error: string;
            success: string;
            warning: string;
            info: string;
            required: string;
            optional: string;
            email: string;
            password: string;
            name: string;
            firstName: string;
            lastName: string;
            phone: string;
            address: string;
            city: string;
            state: string;
            cityAndState: string;
            zipCode: string;
            country: string;
            date: string;
            time: string;
            dateTime: string;
            description: string;
            notes: string;
            status: string;
            active: string;
            inactive: string;
            enabled: string;
            disabled: string;
            all: string;
            none: string;
            select: string;
            selected: string;
            total: string;
            count: string;
            from: string;
            to: string;
            of: string;
            languageEnglish: string;
            languageSpanish: string;
            languageTemplateEnglish: string;
            languageTemplateSpanish: string;
        };
        messages: {
            loading: string;
            noData: string;
            noResults: string;
            errorOccurred: string;
            tryAgain: string;
            saved: string;
            deleted: string;
            updated: string;
            created: string;
            confirmDelete: string;
            unsavedChanges: string;
            requiredField: string;
            invalidEmail: string;
            invalidPhone: string;
            invalidPassword: string;
            passwordMismatch: string;
            networkError: string;
            serverError: string;
            unauthorized: string;
            forbidden: string;
            notFound: string;
            timeout: string;
        };
        navigation: {
            home: string;
            dashboard: string;
            services: string;
            signatures: string;
            ipads: string;
            account: string;
            settings: string;
            profile: string;
            logout: string;
            help: string;
            support: string;
            documentation: string;
            about: string;
            contact: string;
            privacy: string;
            terms: string;
            marketing: string;
        };
    };

    // Authentication pages
    Auth: {
        login: {
            title: string;
            subtitle: string;
            emailLabel: string;
            emailPlaceholder: string;
            passwordLabel: string;
            passwordPlaceholder: string;
            rememberMe: string;
            forgotPassword: string;
            sendLoginCode: string;
            sending: string;
            needAccount: string;
            registerLink: string;
            codeEmailMessage: string;
            loginWithGoogle: string;
            loginWithApple: string;
            or: string;
            errorMessage: string;
            signIn: string;
            signingIn: string;
            signInWithPassword: string;
            signInWithCode: string;
        };
        loginConfirm: {
            title: string;
            subtitle: string;
            codeLabel: string;
            codePlaceholder: string;
            confirmButton: string;
            confirming: string;
            resendCode: string;
            resending: string;
            changeEmail: string;
            invalidCode: string;
            codeSent: string;
            codeExpired: string;
        };
        register: {
            title: string;
            subtitle: string;
            firstNameLabel: string;
            firstNamePlaceholder: string;
            lastNameLabel: string;
            lastNamePlaceholder: string;
            emailLabel: string;
            emailPlaceholder: string;
            passwordLabel: string;
            passwordPlaceholder: string;
            confirmPasswordLabel: string;
            confirmPasswordPlaceholder: string;
            agreeTerms: string;
            registerButton: string;
            registering: string;
            haveAccount: string;
            loginLink: string;
            accountCreated: string;
            verifyEmail: string;
        };
        forgotPassword: {
            title: string;
            subtitle: string;
            sendButton: string;
            sending: string;
            checkEmail: string;
            successMessage: string;
            backToLogin: string;
        };
        resetPassword: {
            title: string;
            subtitle: string;
            newPasswordLabel: string;
            newPasswordPlaceholder: string;
            confirmPasswordLabel: string;
            confirmPasswordPlaceholder: string;
            resetButton: string;
            resetting: string;
            successTitle: string;
            successMessage: string;
            redirectMessage: string;
            invalidLinkTitle: string;
            invalidLinkMessage: string;
            requestNewLink: string;
            passwordMismatch: string;
            passwordTooShort: string;
            autoLoginMessage: string;
        };
        logout: {
            title: string;
            message: string;
            loggingOut: string;
            loggedOut: string;
        };
    };

    // Service pages
    Services: {
        list: {
            title: string;
            viewDashboard: string;
            viewList: string;
            createService: string;
            searchPlaceholder: string;
            filterByStatus: string;
            allStatuses: string;
            active: string;
            ended: string;
            scheduled: string;
            noServices: string;
            serviceFor: string;
            created: string;
            lastActivity: string;
            signatures: string;
            actions: string;
            view: string;
            edit: string;
            end: string;
            delete: string;
            confirmEnd: string;
            confirmDelete: string;
            moreActions: string;
        };
        view: {
            title: string;
            details: string;
            signatures: string;
            qrCode: string;
            familyAccess: string;
            photos: string;
            books: string;
            pdfs: string;
            sendToIPad: string;
            sendToFamily: string;
            endService: string;
            deleteService: string;
            serviceInfo: string;
            deceasedName: string;
            birthDate: string;
            deathDate: string;
            serviceDate: string;
            location: string;
            status: string;
            created: string;
            lastModified: string;
            totalSignatures: string;
            viewSignatures: string;
            downloadPDF: string;
            shareFamilyLink: string;
            copyLink: string;
            linkCopied: string;
        };
        signatures: {
            title: string;
            for: string;
            total: string;
            searchPlaceholder: string;
            filterByType: string;
            allTypes: string;
            handwritten: string;
            typed: string;
            name: string;
            message: string;
            signedAt: string;
            type: string;
            actions: string;
            view: string;
            delete: string;
            confirmDelete: string;
            noSignatures: string;
            exportCSV: string;
            exportPDF: string;
            viewSignature: string;
            closeModal: string;
            loadMore: string;
            retranscribe: string;
        };
        create: {
            title: string;
            subtitle: string;
            creating: string;
            updating: string;
            serviceCreated: string;
            serviceUpdated: string;
        };
    };

    // Already exists - keeping as is
    CreateService: {
        title: string;
        subtitle: string;
        form: CreateServiceFormCopy;
    };

    // iPad Management
    IPads: {
        list: {
            title: string;
            addIPad: string;
            searchPlaceholder: string;
            filterByStatus: string;
            allStatuses: string;
            active: string;
            inactive: string;
            setupPending: string;
            noIPads: string;
            name: string;
            status: string;
            activeService: string;
            lastActivity: string;
            actions: string;
            view: string;
            edit: string;
            setup: string;
            deactivate: string;
            activate: string;
            delete: string;
            confirmDelete: string;
            confirmDeactivate: string;
            moreActions: string;
        };
        add: {
            title: string;
            subtitle: string;
            nameLabel: string;
            namePlaceholder: string;
            descriptionLabel: string;
            descriptionPlaceholder: string;
            addButton: string;
            adding: string;
            iPadAdded: string;
            setupInstructions: string;
        };
        setup: {
            title: string;
            subtitle: string;
            step1: string;
            step2: string;
            step3: string;
            step4: string;
            deviceCode: string;
            copyCode: string;
            codeCopied: string;
            completeSetup: string;
            completing: string;
            setupComplete: string;
            troubleshooting: string;
        };
        manage: {
            title: string;
            details: string;
            activity: string;
            services: string;
            settings: string;
            name: string;
            description: string;
            status: string;
            deviceId: string;
            lastActive: string;
            totalServices: string;
            totalSignatures: string;
            currentService: string;
            noCurrentService: string;
            sendService: string;
            endService: string;
            update: string;
            updating: string;
            updated: string;
        };
    };

    // Account Management
    Account: {
        details: {
            title: string;
            accountInfo: string;
            businessName: string;
            contactName: string;
            email: string;
            phone: string;
            address: string;
            logo: string;
            uploadLogo: string;
            removeLogo: string;
            update: string;
            updating: string;
            updated: string;
        };
        billing: {
            title: string;
            currentPlan: string;
            planDetails: string;
            signatures: string;
            users: string;
            ipads: string;
            price: string;
            billingCycle: string;
            nextBilling: string;
            paymentMethod: string;
            updatePayment: string;
            changePlan: string;
            choosePlan: string;
            subscribe: string;
            confirmPlanChange: string;
            cancelSubscription: string;
            invoices: string;
            date: string;
            amount: string;
            status: string;
            download: string;
            noInvoices: string;
            banner: {
                freeTrialExpiring: string;
                freeTrialExpiringSoon: string;
                freeTrialExpiredToday: string;
                freeTrialExpired: string;
                upgradeNow: string;
                daysRemaining: string;
                dayRemaining: string;
            };
        };
        users: {
            title: string;
            addUser: string;
            searchPlaceholder: string;
            filterByRole: string;
            allRoles: string;
            admin: string;
            user: string;
            noUsers: string;
            name: string;
            email: string;
            role: string;
            status: string;
            lastActive: string;
            actions: string;
            edit: string;
            remove: string;
            confirmRemove: string;
            invitePending: string;
            resendInvite: string;
            cancelInvite: string;
        };
        addUser: {
            title: string;
            subtitle: string;
            emailLabel: string;
            emailPlaceholder: string;
            roleLabel: string;
            sendInvite: string;
            sending: string;
            inviteSent: string;
            userExists: string;
        };
    };

    // Public Pages
    Public: {
        sign: {
            title: string;
            subtitle: string;
            nameLabel: string;
            namePlaceholder: string;
            messageLabel: string;
            messagePlaceholder: string;
            signMethod: string;
            drawSignature: string;
            typeSignature: string;
            clearSignature: string;
            submitSignature: string;
            submitting: string;
            signatureSubmitted: string;
            thankYou: string;
            thankYouMessage: string;
            returnMessage: string;
            requiredName: string;
            requiredSignature: string;
            drawPrompt: string;
            preplanningOption: string;
            emailLabel: string;
            emailPlaceholder: string;
            phoneLabel: string;
            phonePlaceholder: string;
            mailingAddressLabel: string;
            mailingAddressLine1Placeholder: string;
            mailingAddressLine2Placeholder: string;
            signGuestbookLegend: string;
            signing: string;
            signGuestbook: string;
            interestedInPlanningLabel: string;
            interestedInPlanningText: string;
        };
        family: {
            title: string;
            subtitle: string;
            forFamily: string;
            viewSignatures: string;
            totalSignatures: string;
            shareGuestbook: string;
            downloadPDF: string;
            searchSignatures: string;
            noSignaturesYet: string;
            firstToSign: string;
            signGuestbook: string;
            name: string;
            message: string;
            signedOn: string;
            showMore: string;
            showLess: string;
            loading: string;
            virtualSignatures: string;
            nameNotAvailable: string;
            phoneNone: string;
            emailNone: string;
            waitingForTranscription: string;
            thankYouMessage: string;
        };
    };

    // Error Messages
    Errors: {
        generic: string;
        network: string;
        server: string;
        notFound: string;
        unauthorized: string;
        forbidden: string;
        validation: string;
        required: string;
        invalidFormat: string;
        tooShort: string;
        tooLong: string;
        passwordWeak: string;
        emailInvalid: string;
        phoneInvalid: string;
        dateInvalid: string;
        fileTooBig: string;
        fileTypeInvalid: string;
        uploadFailed: string;
        downloadFailed: string;
        timeout: string;
        offline: string;
        maintenance: string;
    };

    // Success Messages
    Success: {
        saved: string;
        updated: string;
        deleted: string;
        created: string;
        sent: string;
        uploaded: string;
        downloaded: string;
        copied: string;
        shared: string;
        completed: string;
        activated: string;
        deactivated: string;
        approved: string;
        rejected: string;
        subscribed: string;
        unsubscribed: string;
    };

    // Form Validation
    Validation: {
        required: string;
        email: string;
        phone: string;
        password: string;
        passwordMatch: string;
        minLength: string;
        maxLength: string;
        minValue: string;
        maxValue: string;
        alphanumeric: string;
        numeric: string;
        alphabetic: string;
        url: string;
        date: string;
        time: string;
        fileSize: string;
        fileType: string;
        unique: string;
        agreement: string;
    };

    // Modal Dialogs
    Modals: {
        confirm: {
            title: string;
            message: string;
            confirmButton: string;
            cancelButton: string;
        };
        delete: {
            title: string;
            message: string;
            warning: string;
            confirmButton: string;
            cancelButton: string;
        };
        unsavedChanges: {
            title: string;
            message: string;
            saveButton: string;
            discardButton: string;
            cancelButton: string;
        };
        error: {
            title: string;
            message: string;
            tryAgainButton: string;
            closeButton: string;
        };
        success: {
            title: string;
            message: string;
            continueButton: string;
        };
    };

    // Date/Time
    DateTime: {
        today: string;
        yesterday: string;
        tomorrow: string;
        thisWeek: string;
        lastWeek: string;
        nextWeek: string;
        thisMonth: string;
        lastMonth: string;
        nextMonth: string;
        thisYear: string;
        lastYear: string;
        nextYear: string;
        days: string[];
        daysShort: string[];
        months: string[];
        monthsShort: string[];
        am: string;
        pm: string;
        dateFormat: string;
        timeFormat: string;
        dateTimeFormat: string;
    };

    // Pagination
    Pagination: {
        showing: string;
        of: string;
        entries: string;
        page: string;
        pages: string;
        previous: string;
        next: string;
        first: string;
        last: string;
        goToPage: string;
        rowsPerPage: string;
    };

    // Empty States
    EmptyStates: {
        noServices: string;
        noSignatures: string;
        noIPads: string;
        noUsers: string;
        noInvoices: string;
        noResults: string;
        noData: string;
        createFirst: string;
        inviteFirst: string;
        connectFirst: string;
        searchDifferent: string;
        clearFilters: string;
    };

    // Layout Errors
    Layout: {
        serviceRequired: string;
        unableToLoadService: string;
        retry: string;
    };

    // Help/Tooltips
    Help: {
        servicePhoto: string;
        preferredName: string;
        preplanningOption: string;
        iPadSetup: string;
        familyAccess: string;
        signatureTypes: string;
        exportOptions: string;
        userRoles: string;
        billing: string;
        support: string;
    };

    // Management section for agency admins and super users
    Management: {
        title: string;
        viewAccounts: string;
        viewAllAccounts: string;
        addAccount: string;
        viewUsers: string;
    };

    // Admin section for super users
    Admin: {
        title: string;
        viewAgencies: string;
        addAgency: string;
        manageCrops: string;
        generatedPDFs: string;
        systemSettings: string;
    };

    // Books section
    Books: {
        title: string;
        viewBooks: string;
        createBook: string;
    };
    
    // Follow-up Messages section
    FollowUpMessages: {
        title: string;
        subtitle: string;
        enable: {
            title: string;
            description: string;
            emailLabel: string;
            emailDescription: string;
            enableButton: string;
            enabling: string;
            helpText: string;
        };
        tabs: {
            reviewQueue: string;
            createMessage: string;
            history: string;
            analytics: string;
            settings: string;
        };
        training: {
            modeBanner: string;
            progress: string;
            messagesRemaining: string;
            messageRemaining: string;
        };
    };
};

/** ------------------------------------------------------------------ */
/** 2️⃣  The localized text for each language.                          */
/** ------------------------------------------------------------------ */
const localizedCopy: Record<Language, LocalizedCopy> = {
    [Language.EN_US]: {
        // Common UI elements
        Common: {
            buttons: {
                save: "Save",
                cancel: "Cancel",
                delete: "Delete",
                edit: "Edit",
                close: "Close",
                confirm: "Confirm",
                back: "Back",
                next: "Next",
                submit: "Submit",
                add: "Add",
                remove: "Remove",
                view: "View",
                download: "Download",
                upload: "Upload",
                print: "Print",
                share: "Share",
                export: "Export",
                import: "Import",
                refresh: "Refresh",
                search: "Search",
                filter: "Filter",
                clear: "Clear",
                reset: "Reset",
                continue: "Continue",
                finish: "Finish",
                create: "Create",
                update: "Update",
                ok: "OK",
                yes: "Yes",
                no: "No",
            },
            labels: {
                loading: "Loading",
                saving: "Saving",
                deleting: "Deleting",
                processing: "Processing",
                error: "Error",
                success: "Success",
                warning: "Warning",
                info: "Info",
                required: "Required",
                optional: "Optional",
                email: "Email",
                password: "Password",
                name: "Your Name",
                firstName: "First Name",
                lastName: "Last Name",
                phone: "Your Phone Number",
                address: "Address",
                city: "City",
                state: "State",
                cityAndState: "City and State",
                zipCode: "ZIP Code",
                country: "Country",
                date: "Date",
                time: "Time",
                dateTime: "Date & Time",
                description: "Description",
                notes: "Notes",
                status: "Status",
                active: "Active",
                inactive: "Inactive",
                enabled: "Enabled",
                disabled: "Disabled",
                all: "All",
                none: "None",
                select: "Select",
                selected: "Selected",
                total: "Total",
                count: "Count",
                from: "From",
                to: "To",
                of: "of",
                languageEnglish: "English",
                languageSpanish: "Español",
                languageTemplateEnglish: "(iPad templates will display in English)",
                languageTemplateSpanish: "(iPad templates will display in Spanish)",
            },
            messages: {
                loading: "Loading...",
                noData: "No data available",
                noResults: "No results found",
                errorOccurred: "An error occurred",
                tryAgain: "Please try again",
                saved: "Successfully saved",
                deleted: "Successfully deleted",
                updated: "Successfully updated",
                created: "Successfully created",
                confirmDelete: "Are you sure you want to delete this?",
                unsavedChanges: "You have unsaved changes",
                requiredField: "This field is required",
                invalidEmail: "Please enter a valid email address",
                invalidPhone: "Please enter a valid phone number",
                invalidPassword: "Password does not meet requirements",
                passwordMismatch: "Passwords do not match",
                networkError: "Network error occurred",
                serverError: "Server error occurred",
                unauthorized: "You are not authorized",
                forbidden: "Access forbidden",
                notFound: "Not found",
                timeout: "Request timed out",
            },
            navigation: {
                home: "Home",
                dashboard: "Dashboard",
                services: "Services",
                signatures: "Signatures",
                ipads: "iPads",
                account: "Account",
                settings: "Settings",
                profile: "Profile",
                logout: "Logout",
                help: "Help",
                support: "Support",
                documentation: "Documentation",
                about: "About",
                contact: "Contact",
                privacy: "Privacy",
                terms: "Terms",
                marketing: "Marketing",
            },
        },

        // Authentication pages
        Auth: {
            login: {
                title: "Sign in to Signature Guestbook",
                subtitle: "Enter your credentials to continue",
                emailLabel: "Enter your email address",
                emailPlaceholder: "email@email.com",
                passwordLabel: "Password",
                passwordPlaceholder: "Enter Password",
                rememberMe: "Remember me",
                forgotPassword: "Forgot Password?",
                sendLoginCode: "Send Login Code",
                sending: "Sending...",
                needAccount: "Need an account?",
                registerLink: "Register account",
                codeEmailMessage: "We will email you a code to login.",
                loginWithGoogle: "Use Google",
                loginWithApple: "Use Apple",
                or: "Or",
                errorMessage: "An error occurred. Please try again.",
                signIn: "Sign In",
                signingIn: "Signing in...",
                signInWithPassword: "Sign in with password",
                signInWithCode: "Sign in with email code",
            },
            loginConfirm: {
                title: "Confirm Login",
                subtitle: "Enter the code sent to your email",
                codeLabel: "Enter confirmation code",
                codePlaceholder: "000000",
                confirmButton: "Confirm",
                confirming: "Confirming...",
                resendCode: "Resend code",
                resending: "Resending...",
                changeEmail: "Change email",
                invalidCode: "Invalid code",
                codeSent: "Code sent successfully",
                codeExpired: "Code has expired",
            },
            register: {
                title: "Create Account",
                subtitle: "Join Signature Guestbook today",
                firstNameLabel: "First Name",
                firstNamePlaceholder: "John",
                lastNameLabel: "Last Name",
                lastNamePlaceholder: "Doe",
                emailLabel: "Your Email Address",
                emailPlaceholder: "john@example.com",
                passwordLabel: "Password",
                passwordPlaceholder: "Create a password",
                confirmPasswordLabel: "Confirm Password",
                confirmPasswordPlaceholder: "Confirm your password",
                agreeTerms: "I agree to the terms and conditions",
                registerButton: "Create Account",
                registering: "Creating account...",
                haveAccount: "Already have an account?",
                loginLink: "Sign in",
                accountCreated: "Account created successfully",
                verifyEmail: "Please verify your email",
            },
            forgotPassword: {
                title: "Forgot Password",
                subtitle: "Enter your email address and we'll send you instructions to reset your password.",
                sendButton: "Send Reset Link",
                sending: "Sending...",
                checkEmail: "Check Your Email",
                successMessage: "If an account exists with this email, you will receive password reset instructions.",
                backToLogin: "Back to Login",
            },
            resetPassword: {
                title: "Reset Password",
                subtitle: "Enter your new password below.",
                newPasswordLabel: "New Password",
                newPasswordPlaceholder: "Enter new password",
                confirmPasswordLabel: "Confirm Password",
                confirmPasswordPlaceholder: "Confirm new password",
                resetButton: "Reset Password",
                resetting: "Resetting...",
                successTitle: "Password Reset Successfully",
                successMessage: "Your password has been reset successfully. You can now sign in.",
                redirectMessage: "Your password has been reset. Redirecting to login...",
                invalidLinkTitle: "Invalid Reset Link",
                invalidLinkMessage: "This password reset link is invalid or has expired.",
                requestNewLink: "Request a New Link",
                passwordMismatch: "Passwords do not match",
                passwordTooShort: "Password must be at least 5 characters",
                autoLoginMessage: "Your password has been reset. Logging you in...",
            },
            logout: {
                title: "Logout",
                message: "Are you sure you want to logout?",
                loggingOut: "Logging out...",
                loggedOut: "Successfully logged out",
            },
        },

        // Service pages
        Services: {
            list: {
                title: "View Services",
                viewDashboard: "View Services Dashboard",
                viewList: "View Services List",
                createService: "Create Service",
                searchPlaceholder: "Search services...",
                filterByStatus: "Filter by status",
                allStatuses: "All Statuses",
                active: "Active",
                ended: "Ended",
                scheduled: "Scheduled",
                noServices: "No services found",
                serviceFor: "Service for",
                created: "Created",
                lastActivity: "Last Activity",
                signatures: "Signatures",
                actions: "Actions",
                view: "View",
                edit: "Edit",
                end: "End",
                delete: "Delete",
                confirmEnd: "Are you sure you want to end this service?",
                confirmDelete: "Are you sure you want to delete this service?",
                moreActions: "More Actions",
            },
            view: {
                title: "Service Details",
                details: "Details",
                signatures: "Signatures",
                qrCode: "QR Code",
                familyAccess: "Family Access",
                photos: "Photos",
                books: "Books",
                pdfs: "PDFs",
                sendToIPad: "Send to iPad",
                sendToFamily: "Send to Family",
                endService: "End Service",
                deleteService: "Delete Service",
                serviceInfo: "Service Information",
                deceasedName: "Deceased Name",
                birthDate: "Birth Date",
                deathDate: "Death Date",
                serviceDate: "Service Date",
                location: "Location",
                status: "Status",
                created: "Created",
                lastModified: "Last Modified",
                totalSignatures: "Total Signatures",
                viewSignatures: "View Signatures",
                downloadPDF: "Download PDF",
                shareFamilyLink: "Share Family Link",
                copyLink: "Copy Link",
                linkCopied: "Link copied to clipboard",
            },
            signatures: {
                title: "Signatures",
                for: "for",
                total: "Total",
                searchPlaceholder: "Search signatures...",
                filterByType: "Filter by type",
                allTypes: "All Types",
                handwritten: "Handwritten",
                typed: "Typed",
                name: "Name",
                message: "Message",
                signedAt: "Signed At",
                type: "Type",
                actions: "Actions",
                view: "View",
                delete: "Delete",
                confirmDelete: "Are you sure you want to delete this signature?",
                noSignatures: "No signatures found",
                exportCSV: "Export CSV",
                exportPDF: "Export PDF",
                viewSignature: "View Signature",
                closeModal: "Close",
                loadMore: "Load more",
                retranscribe: "Retranscribe",
            },
            create: {
                title: "Create Service",
                subtitle: "Enter service details",
                creating: "Creating service...",
                updating: "Updating service...",
                serviceCreated: "Service created successfully",
                serviceUpdated: "Service updated successfully",
            },
        },

        CreateService: {
            title: "Create Service",
            subtitle: "Enter service details",
            form: {
                photoLabel: "Deceased Person's Photo (required)",
                useSampleAvatarLink: "Use a sample avatar",
                selectSampleAvatarLabel: "Select a sample avatar",

                fullNameLabel: "Deceased Person's Full Name (required)",
                fullNamePlaceholder: "John Doe",

                preferredNameLabel:
                    "Deceased Person's Preferred Name (a first name or nickname)",
                preferredNamePlaceholder: "John",

                birthDateLabel: "Deceased Person's Birth Date",
                deathDateLabel: "Deceased Person's Death Date",

                hidePreplanningCheckboxLabel: "Hide the pre-planning checkbox",
                defaultPreplanningCheckboxLabel: "Default pre-planning checkbox to checked",
                preplanningLabel: "Pre-planning checkbox label",
                preplanningLabelPlaceholder:
                    "I would like information on funeral planning",

                mobileLabelHeaderLabel: "Pre-Planning Question - Section Header (Mobile)",
                mobileLabelHeaderPlaceholder: "Interested in Planning Ahead",

                mobileQuestionLabel: "Pre-Planning Question - Text (Mobile)",
                mobileQuestionPlaceholder:
                    "Yes, I'm interested in planning ahead for my own arrangements.",

                viewAdvancedOptionsLabel: "View advanced options",

                createButton: "Create Service",
                updateButton: "Update Service",

                savingCreateMsg:
                    "Your service is being created. This takes about 10 seconds…",
                savingUpdateMsg:
                    "Your service is being updated. This takes about 10 seconds…",
            },
        },

        // iPad Management
        IPads: {
            list: {
                title: "View iPads",
                addIPad: "Add iPad",
                searchPlaceholder: "Search iPads...",
                filterByStatus: "Filter by status",
                allStatuses: "All Statuses",
                active: "Active",
                inactive: "Inactive",
                setupPending: "Setup Pending",
                noIPads: "No iPads found",
                name: "Name",
                status: "Status",
                activeService: "Active Service",
                lastActivity: "Last Activity",
                actions: "Actions",
                view: "View",
                edit: "Edit",
                setup: "Setup",
                deactivate: "Deactivate",
                activate: "Activate",
                delete: "Delete",
                confirmDelete: "Are you sure you want to delete this iPad?",
                confirmDeactivate: "Are you sure you want to deactivate this iPad?",
                moreActions: "More Actions",
            },
            add: {
                title: "Add iPad",
                subtitle: "Register a new iPad device",
                nameLabel: "iPad Name",
                namePlaceholder: "Front Lobby iPad",
                descriptionLabel: "Description",
                descriptionPlaceholder: "Optional description",
                addButton: "Add iPad",
                adding: "Adding iPad...",
                iPadAdded: "iPad added successfully",
                setupInstructions: "Follow the setup instructions to complete registration",
            },
            setup: {
                title: "Setup iPad",
                subtitle: "Complete the setup process on your iPad device",
                step1: "Open the Signature Guestbook app on the iPad",
                step2: "Tap on 'Setup Device'",
                step3: "When prompted for an email address, enter this code instead:",
                step4: "Tap 'Complete Setup'",
                deviceCode: "Device Code",
                copyCode: "Copy Code",
                codeCopied: "Code copied to clipboard",
                completeSetup: "Complete Setup",
                completing: "Completing setup...",
                setupComplete: "iPad setup completed successfully",
                troubleshooting: "Setup iPad manually",
            },
            manage: {
                title: "Manage iPad",
                details: "Details",
                activity: "Activity",
                services: "Services",
                settings: "Settings",
                name: "Name",
                description: "Description",
                status: "Status",
                deviceId: "Device ID",
                lastActive: "Last Active",
                totalServices: "Total Services",
                totalSignatures: "Total Signatures",
                currentService: "Current Service",
                noCurrentService: "No active service",
                sendService: "Send Service",
                endService: "End Service",
                update: "Update",
                updating: "Updating...",
                updated: "iPad updated successfully",
            },
        },

        // Account Management
        Account: {
            details: {
                title: "Account Details",
                accountInfo: "Account Information",
                businessName: "Funeral Home / Cemetery Name",
                contactName: "Contact Name",
                email: "Email",
                phone: "Phone",
                address: "Address",
                logo: "Logo",
                uploadLogo: "Upload Logo",
                removeLogo: "Remove Logo",
                update: "Update",
                updating: "Updating...",
                updated: "Account updated successfully",
            },
            billing: {
                title: "Billing & Subscription",
                currentPlan: "Current Plan",
                planDetails: "Plan Details",
                signatures: "Signatures",
                users: "Users",
                ipads: "iPads",
                price: "Price",
                billingCycle: "Billing Cycle",
                nextBilling: "Next Billing Date",
                paymentMethod: "Payment Method",
                updatePayment: "Update Payment Method",
                changePlan: "Change Plan",
                choosePlan: "Choose Your Plan",
                subscribe: "Subscribe",
                confirmPlanChange: "Confirm Plan Change",
                cancelSubscription: "Cancel Subscription",
                invoices: "Invoices",
                date: "Date",
                amount: "Amount",
                status: "Status",
                download: "Download",
                noInvoices: "No invoices found",
                banner: {
                    freeTrialExpiring: "Your free trial expires in {days} days. Upgrade now to continue using all features.",
                    freeTrialExpiringSoon: "Your free trial expires soon. Upgrade now to avoid service interruption.",
                    freeTrialExpiredToday: "Your free trial expires today. Upgrade now to continue service.",
                    freeTrialExpired: "Your free trial has expired. Upgrade now to restore full access.",
                    upgradeNow: "Upgrade Now",
                    daysRemaining: "{days} days remaining",
                    dayRemaining: "1 day remaining",
                },
            },
            users: {
                title: "Manage Users",
                addUser: "Add User",
                searchPlaceholder: "Search users...",
                filterByRole: "Filter by role",
                allRoles: "All Roles",
                admin: "Admin",
                user: "User",
                noUsers: "No users found",
                name: "Name",
                email: "Email",
                role: "Role",
                status: "Status",
                lastActive: "Last Active",
                actions: "Actions",
                edit: "Edit",
                remove: "Remove",
                confirmRemove: "Are you sure you want to remove this user?",
                invitePending: "Invite Pending",
                resendInvite: "Resend Invite",
                cancelInvite: "Cancel Invite",
            },
            addUser: {
                title: "Add User",
                subtitle: "Invite a new user to the account",
                emailLabel: "Email Address",
                emailPlaceholder: "user@example.com",
                roleLabel: "Role",
                sendInvite: "Send Invite",
                sending: "Sending invite...",
                inviteSent: "Invite sent successfully",
                userExists: "User already exists",
            },
        },

        // Public Pages
        Public: {
            sign: {
                title: "Sign Guestbook",
                subtitle: "Leave your message",
                nameLabel: "Your Name",
                namePlaceholder: "Enter your name",
                messageLabel: "Your Message",
                messagePlaceholder: "Share your thoughts and memories...",
                signMethod: "Signature Method",
                drawSignature: "Draw Signature",
                typeSignature: "Type Signature",
                clearSignature: "Clear",
                submitSignature: "Submit",
                submitting: "Submitting...",
                signatureSubmitted: "Signature submitted successfully",
                thankYou: "Thank you for signing",
                thankYouMessage: "Thank you for signing the guest registry!",
                returnMessage: "You may now close this page",
                requiredName: "Name is required",
                requiredSignature: "Signature is required",
                drawPrompt: "Sign in the box above",
                preplanningOption: "I would like information on funeral planning",
                emailLabel: "Email (optional)",
                emailPlaceholder: "your@email.com",
                phoneLabel: "Phone (optional)",
                phonePlaceholder: "(555) 123-4567",
                mailingAddressLabel: "Mailing Address (optional)",
                mailingAddressLine1Placeholder: "Line 1, e.g. 123 Main St",
                mailingAddressLine2Placeholder: "Line 2, e.g. Indianapolis, IN 46202",
                signGuestbookLegend: "Sign the signature guestbook:",
                signing: "Signing...",
                signGuestbook: "Sign Guestbook",
                interestedInPlanningLabel: "Interested in Planning Ahead",
                interestedInPlanningText: "Yes, I'm interested in planning ahead for my own arrangements.",
            },
            family: {
                title: "Family Guestbook",
                subtitle: "View messages from friends and family",
                forFamily: "For the family of",
                viewSignatures: "View All Signatures",
                totalSignatures: "Total Signatures",
                shareGuestbook: "Share Guestbook",
                downloadPDF: "Download PDF",
                searchSignatures: "Search signatures...",
                noSignaturesYet: "No signatures yet",
                firstToSign: "Be the first to sign",
                signGuestbook: "Sign Guestbook",
                name: "Name",
                message: "Message",
                signedOn: "Signed on",
                showMore: "Show more",
                showLess: "Show less",
                loading: "Loading...",
                virtualSignatures: "Virtual signatures:",
                nameNotAvailable: "(Name not available)",
                phoneNone: "(none)",
                emailNone: "(none)",
                waitingForTranscription: "(Waiting for signature transcription)",
                thankYouMessage: "Thank you for signing the guest registry!",
            },
        },

        // Error Messages
        Errors: {
            generic: "An error occurred",
            network: "Network error. Please check your connection",
            server: "Server error. Please try again later",
            notFound: "Page not found",
            unauthorized: "Unauthorized access",
            forbidden: "Access forbidden",
            validation: "Validation error",
            required: "This field is required",
            invalidFormat: "Invalid format",
            tooShort: "Too short",
            tooLong: "Too long",
            passwordWeak: "Password is too weak",
            emailInvalid: "Invalid email address",
            phoneInvalid: "Invalid phone number",
            dateInvalid: "Invalid date",
            fileTooBig: "File is too large",
            fileTypeInvalid: "Invalid file type",
            uploadFailed: "Upload failed",
            downloadFailed: "Download failed",
            timeout: "Request timed out",
            offline: "You appear to be offline",
            maintenance: "System under maintenance",
        },

        // Success Messages
        Success: {
            saved: "Saved successfully",
            updated: "Updated successfully",
            deleted: "Deleted successfully",
            created: "Created successfully",
            sent: "Sent successfully",
            uploaded: "Uploaded successfully",
            downloaded: "Downloaded successfully",
            copied: "Copied to clipboard",
            shared: "Shared successfully",
            completed: "Completed successfully",
            activated: "Activated successfully",
            deactivated: "Deactivated successfully",
            approved: "Approved successfully",
            rejected: "Rejected successfully",
            subscribed: "Subscribed successfully",
            unsubscribed: "Unsubscribed successfully",
        },

        // Form Validation
        Validation: {
            required: "{field} is required",
            email: "Please enter a valid email address",
            phone: "Please enter a valid phone number",
            password: "Password must be at least 8 characters",
            passwordMatch: "Passwords do not match",
            minLength: "Must be at least {min} characters",
            maxLength: "Must be no more than {max} characters",
            minValue: "Must be at least {min}",
            maxValue: "Must be no more than {max}",
            alphanumeric: "Must contain only letters and numbers",
            numeric: "Must contain only numbers",
            alphabetic: "Must contain only letters",
            url: "Please enter a valid URL",
            date: "Please enter a valid date",
            time: "Please enter a valid time",
            fileSize: "File must be less than {size}",
            fileType: "File must be {types}",
            unique: "This value is already taken",
            agreement: "You must agree to continue",
        },

        // Modal Dialogs
        Modals: {
            confirm: {
                title: "Confirm Action",
                message: "Are you sure you want to continue?",
                confirmButton: "Confirm",
                cancelButton: "Cancel",
            },
            delete: {
                title: "Delete Confirmation",
                message: "Are you sure you want to delete this?",
                warning: "This action cannot be undone.",
                confirmButton: "Delete",
                cancelButton: "Cancel",
            },
            unsavedChanges: {
                title: "Unsaved Changes",
                message: "You have unsaved changes. What would you like to do?",
                saveButton: "Save Changes",
                discardButton: "Discard Changes",
                cancelButton: "Cancel",
            },
            error: {
                title: "Error",
                message: "An error occurred while processing your request.",
                tryAgainButton: "Try Again",
                closeButton: "Close",
            },
            success: {
                title: "Success",
                message: "Operation completed successfully.",
                continueButton: "Continue",
            },
        },

        // Date/Time
        DateTime: {
            today: "Today",
            yesterday: "Yesterday",
            tomorrow: "Tomorrow",
            thisWeek: "This Week",
            lastWeek: "Last Week",
            nextWeek: "Next Week",
            thisMonth: "This Month",
            lastMonth: "Last Month",
            nextMonth: "Next Month",
            thisYear: "This Year",
            lastYear: "Last Year",
            nextYear: "Next Year",
            days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
            months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
            monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
            am: "AM",
            pm: "PM",
            dateFormat: "MM/DD/YYYY",
            timeFormat: "h:mm A",
            dateTimeFormat: "MM/DD/YYYY h:mm A",
        },

        // Pagination
        Pagination: {
            showing: "Showing",
            of: "of",
            entries: "entries",
            page: "Page",
            pages: "pages",
            previous: "Previous",
            next: "Next",
            first: "First",
            last: "Last",
            goToPage: "Go to page",
            rowsPerPage: "Rows per page",
        },

        // Empty States
        EmptyStates: {
            noServices: "No services found. Create your first service to get started.",
            noSignatures: "No signatures yet. Share the QR code or link to start collecting signatures.",
            noIPads: "No iPads configured. Add an iPad to enable in-person signature collection.",
            noUsers: "No users found. Invite team members to collaborate.",
            noInvoices: "No invoices available.",
            noResults: "No results found for your search.",
            noData: "No data available.",
            createFirst: "Create your first {item} to get started.",
            inviteFirst: "Invite your first {item} to get started.",
            connectFirst: "Connect your first {item} to get started.",
            searchDifferent: "Try searching for something different.",
            clearFilters: "Clear filters to see all results.",
        },

        // Layout Errors
        Layout: {
            serviceRequired: "Service is required.",
            unableToLoadService: "Unable to load service",
            retry: "Retry",
        },

        // Help/Tooltips
        Help: {
            servicePhoto: "Upload a photo of the deceased. This will appear on the signature page and in the guestbook.",
            preferredName: "Enter a nickname or preferred name. This will be used in greetings and messages.",
            preplanningOption: "Enable this to collect contact information from guests interested in pre-planning services.",
            iPadSetup: "Follow the setup instructions on the iPad to complete device registration.",
            familyAccess: "Share this link with family members to view and download the guestbook.",
            signatureTypes: "Guests can either draw their signature or type their name.",
            exportOptions: "Export signatures as PDF for printing or CSV for data analysis.",
            userRoles: "Admins can manage all aspects of the account. Users can create and manage services.",
            billing: "Your subscription will automatically renew each billing period.",
            support: "Need help? Contact our support team at support@signatureguestbook.com",
        },

        // Management section for agency admins and super users
        Management: {
            title: "Management",
            viewAccounts: "View Accounts",
            viewAllAccounts: "View All Accounts",
            addAccount: "Add Account",
            viewUsers: "View Users",
        },

        // Admin section for super users
        Admin: {
            title: "Admin",
            viewAgencies: "View Agencies",
            addAgency: "Add Agency",
            manageCrops: "Manage Crops",
            generatedPDFs: "Generated PDFs",
            systemSettings: "System Settings",
        },

        // Books section
        Books: {
            title: "Books",
            viewBooks: "View Books",
            createBook: "Create Book",
        },
        
        // Follow-up Messages section
        FollowUpMessages: {
            title: "Follow-up Messages",
            subtitle: "Send personalized thank-you messages to service attendees",
            enable: {
                title: "Enable Follow-up Messages",
                description: "Follow-up messages are not enabled for this account. Set your email address below to start sending thank-you messages.",
                emailLabel: "Your Email Address",
                emailDescription: "This will be your unique reply-to address for all email campaigns.",
                enableButton: "Enable Follow-up Messages",
                enabling: "Enabling...",
                helpText: "This feature helps you send personalized thank-you messages to guests who sign the digital guestbook.",
            },
            tabs: {
                reviewQueue: "Review Queue",
                createMessage: "Create Message",
                history: "History",
                analytics: "Analytics",
                settings: "Settings",
            },
            training: {
                modeBanner: "Training Mode:",
                progress: "Progress:",
                messagesRemaining: "more messages need to be approved before automatic suggestions begin.",
                messageRemaining: "more message needs to be approved before automatic suggestions begin.",
            },
        },
    },

    [Language.ES_ES]: {
        // Common UI elements
        Common: {
            buttons: {
                save: "Guardar",
                cancel: "Cancelar",
                delete: "Eliminar",
                edit: "Editar",
                close: "Cerrar",
                confirm: "Confirmar",
                back: "Atrás",
                next: "Siguiente",
                submit: "Enviar",
                add: "Agregar",
                remove: "Remover",
                view: "Ver",
                download: "Descargar",
                upload: "Subir",
                print: "Imprimir",
                share: "Compartir",
                export: "Exportar",
                import: "Importar",
                refresh: "Actualizar",
                search: "Buscar",
                filter: "Filtrar",
                clear: "Limpiar",
                reset: "Restablecer",
                continue: "Continuar",
                finish: "Finalizar",
                create: "Crear",
                update: "Actualizar",
                ok: "OK",
                yes: "Sí",
                no: "No",
            },
            labels: {
                loading: "Cargando",
                saving: "Guardando",
                deleting: "Eliminando",
                processing: "Procesando",
                error: "Error",
                success: "Éxito",
                warning: "Advertencia",
                info: "Información",
                required: "Requerido",
                optional: "Opcional",
                email: "Correo electrónico",
                password: "Contraseña",
                name: "Su Nombre",
                firstName: "Nombre",
                lastName: "Apellido",
                phone: "Su Número de Teléfono",
                address: "Dirección",
                city: "Ciudad",
                state: "Estado",
                cityAndState: "Ciudad y Estado",
                zipCode: "Código postal",
                country: "País",
                date: "Fecha",
                time: "Hora",
                dateTime: "Fecha y hora",
                description: "Descripción",
                notes: "Notas",
                status: "Estado",
                active: "Activo",
                inactive: "Inactivo",
                enabled: "Habilitado",
                disabled: "Deshabilitado",
                all: "Todo",
                none: "Ninguno",
                select: "Seleccionar",
                selected: "Seleccionado",
                total: "Total",
                count: "Cantidad",
                from: "Desde",
                to: "Hasta",
                of: "de",
                languageEnglish: "English",
                languageSpanish: "Español",
                languageTemplateEnglish: "(Las plantillas del iPad se mostrarán en inglés)",
                languageTemplateSpanish: "(Las plantillas del iPad se mostrarán en español)",
            },
            messages: {
                loading: "Cargando...",
                noData: "No hay datos disponibles",
                noResults: "No se encontraron resultados",
                errorOccurred: "Ocurrió un error",
                tryAgain: "Por favor, inténtelo de nuevo",
                saved: "Guardado exitosamente",
                deleted: "Eliminado exitosamente",
                updated: "Actualizado exitosamente",
                created: "Creado exitosamente",
                confirmDelete: "¿Está seguro de que desea eliminar esto?",
                unsavedChanges: "Tiene cambios sin guardar",
                requiredField: "Este campo es requerido",
                invalidEmail: "Por favor, ingrese un correo electrónico válido",
                invalidPhone: "Por favor, ingrese un número de teléfono válido",
                invalidPassword: "La contraseña no cumple los requisitos",
                passwordMismatch: "Las contraseñas no coinciden",
                networkError: "Ocurrió un error de red",
                serverError: "Ocurrió un error del servidor",
                unauthorized: "No está autorizado",
                forbidden: "Acceso prohibido",
                notFound: "No encontrado",
                timeout: "Tiempo de espera agotado",
            },
            navigation: {
                home: "Inicio",
                dashboard: "Panel",
                services: "Servicios",
                signatures: "Firmas",
                ipads: "iPads",
                account: "Cuenta",
                settings: "Configuración",
                profile: "Perfil",
                logout: "Cerrar sesión",
                help: "Ayuda",
                support: "Soporte",
                documentation: "Documentación",
                about: "Acerca de",
                contact: "Contacto",
                privacy: "Privacidad",
                terms: "Términos",
                marketing: "Marketing",
            },
        },

        // Authentication pages
        Auth: {
            login: {
                title: "Iniciar sesión en Signature Guestbook",
                subtitle: "Ingrese sus credenciales para continuar",
                emailLabel: "Ingrese su correo electrónico",
                emailPlaceholder: "correo@correo.com",
                passwordLabel: "Contraseña",
                passwordPlaceholder: "Ingrese su contraseña",
                rememberMe: "Recordarme",
                forgotPassword: "¿Olvidó su contraseña?",
                sendLoginCode: "Enviar código de acceso",
                sending: "Enviando...",
                needAccount: "¿Necesita una cuenta?",
                registerLink: "Registrar cuenta",
                codeEmailMessage: "Le enviaremos un código por correo para iniciar sesión.",
                loginWithGoogle: "Usar Google",
                loginWithApple: "Usar Apple",
                or: "O",
                errorMessage: "Ocurrió un error. Por favor, inténtelo de nuevo.",
                signIn: "Iniciar sesión",
                signingIn: "Iniciando sesión...",
                signInWithPassword: "Iniciar sesión con contraseña",
                signInWithCode: "Iniciar sesión con código de correo",
            },
            loginConfirm: {
                title: "Confirmar inicio de sesión",
                subtitle: "Ingrese el código enviado a su correo",
                codeLabel: "Ingrese el código de confirmación",
                codePlaceholder: "000000",
                confirmButton: "Confirmar",
                confirming: "Confirmando...",
                resendCode: "Reenviar código",
                resending: "Reenviando...",
                changeEmail: "Cambiar correo",
                invalidCode: "Código inválido",
                codeSent: "Código enviado exitosamente",
                codeExpired: "El código ha expirado",
            },
            register: {
                title: "Crear cuenta",
                subtitle: "Únase a Signature Guestbook hoy",
                firstNameLabel: "Nombre",
                firstNamePlaceholder: "Juan",
                lastNameLabel: "Apellido",
                lastNamePlaceholder: "Pérez",
                emailLabel: "Su Correo Electrónico",
                emailPlaceholder: "juan@ejemplo.com",
                passwordLabel: "Contraseña",
                passwordPlaceholder: "Cree una contraseña",
                confirmPasswordLabel: "Confirmar contraseña",
                confirmPasswordPlaceholder: "Confirme su contraseña",
                agreeTerms: "Acepto los términos y condiciones",
                registerButton: "Crear cuenta",
                registering: "Creando cuenta...",
                haveAccount: "¿Ya tiene una cuenta?",
                loginLink: "Iniciar sesión",
                accountCreated: "Cuenta creada exitosamente",
                verifyEmail: "Por favor verifique su correo electrónico",
            },
            forgotPassword: {
                title: "¿Olvidó su contraseña?",
                subtitle: "Ingrese su correo electrónico y le enviaremos instrucciones para restablecer su contraseña.",
                sendButton: "Enviar enlace de restablecimiento",
                sending: "Enviando...",
                checkEmail: "Revise su correo",
                successMessage: "Si existe una cuenta con este correo, recibirá instrucciones para restablecer su contraseña.",
                backToLogin: "Volver a iniciar sesión",
            },
            resetPassword: {
                title: "Restablecer contraseña",
                subtitle: "Ingrese su nueva contraseña a continuación.",
                newPasswordLabel: "Nueva contraseña",
                newPasswordPlaceholder: "Ingrese nueva contraseña",
                confirmPasswordLabel: "Confirmar contraseña",
                confirmPasswordPlaceholder: "Confirme nueva contraseña",
                resetButton: "Restablecer contraseña",
                resetting: "Restableciendo...",
                successTitle: "Contraseña restablecida exitosamente",
                successMessage: "Su contraseña ha sido restablecida exitosamente. Ahora puede iniciar sesión.",
                redirectMessage: "Su contraseña ha sido restablecida. Redirigiendo a iniciar sesión...",
                invalidLinkTitle: "Enlace de restablecimiento inválido",
                invalidLinkMessage: "Este enlace de restablecimiento de contraseña es inválido o ha expirado.",
                requestNewLink: "Solicitar un nuevo enlace",
                passwordMismatch: "Las contraseñas no coinciden",
                passwordTooShort: "La contraseña debe tener al menos 5 caracteres",
                autoLoginMessage: "Su contraseña ha sido restablecida. Iniciando sesión...",
            },
            logout: {
                title: "Cerrar sesión",
                message: "¿Está seguro de que desea cerrar sesión?",
                loggingOut: "Cerrando sesión...",
                loggedOut: "Sesión cerrada exitosamente",
            },
        },

        // Service pages
        Services: {
            list: {
                title: "Ver Servicios",
                viewDashboard: "Ver Panel de Servicios",
                viewList: "Ver Lista de Servicios",
                createService: "Crear Servicio",
                searchPlaceholder: "Buscar servicios...",
                filterByStatus: "Filtrar por estado",
                allStatuses: "Todos los estados",
                active: "Activo",
                ended: "Finalizado",
                scheduled: "Programado",
                noServices: "No se encontraron servicios",
                serviceFor: "Servicio para",
                created: "Creado",
                lastActivity: "Última actividad",
                signatures: "Firmas",
                actions: "Acciones",
                view: "Ver",
                edit: "Editar",
                end: "Finalizar",
                delete: "Eliminar",
                confirmEnd: "¿Está seguro de que desea finalizar este servicio?",
                confirmDelete: "¿Está seguro de que desea eliminar este servicio?",
                moreActions: "Más acciones",
            },
            view: {
                title: "Detalles del servicio",
                details: "Detalles",
                signatures: "Firmas",
                qrCode: "Código QR",
                familyAccess: "Acceso familiar",
                photos: "Fotos",
                books: "Libros",
                pdfs: "PDFs",
                sendToIPad: "Enviar a iPad",
                sendToFamily: "Enviar a familia",
                endService: "Finalizar servicio",
                deleteService: "Eliminar servicio",
                serviceInfo: "Información del servicio",
                deceasedName: "Nombre del fallecido",
                birthDate: "Fecha de nacimiento",
                deathDate: "Fecha de fallecimiento",
                serviceDate: "Fecha del servicio",
                location: "Ubicación",
                status: "Estado",
                created: "Creado",
                lastModified: "Última modificación",
                totalSignatures: "Total de firmas",
                viewSignatures: "Ver firmas",
                downloadPDF: "Descargar PDF",
                shareFamilyLink: "Compartir enlace familiar",
                copyLink: "Copiar enlace",
                linkCopied: "Enlace copiado al portapapeles",
            },
            signatures: {
                title: "Firmas",
                for: "para",
                total: "Total",
                searchPlaceholder: "Buscar firmas...",
                filterByType: "Filtrar por tipo",
                allTypes: "Todos los tipos",
                handwritten: "Manuscrita",
                typed: "Escrita",
                name: "Nombre",
                message: "Mensaje",
                signedAt: "Firmado el",
                type: "Tipo",
                actions: "Acciones",
                view: "Ver",
                delete: "Eliminar",
                confirmDelete: "¿Está seguro de que desea eliminar esta firma?",
                noSignatures: "No se encontraron firmas",
                exportCSV: "Exportar CSV",
                exportPDF: "Exportar PDF",
                viewSignature: "Ver firma",
                closeModal: "Cerrar",
                loadMore: "Cargar más",
                retranscribe: "Retranscribir",
            },
            create: {
                title: "Crear Servicio",
                subtitle: "Ingrese los detalles del servicio",
                creating: "Creando servicio...",
                updating: "Actualizando servicio...",
                serviceCreated: "Servicio creado exitosamente",
                serviceUpdated: "Servicio actualizado exitosamente",
            },
        },

        CreateService: {
            title: "Crear Servicio",
            subtitle: "Ingrese los detalles del servicio",
            form: {
                photoLabel: "Foto de la persona fallecida (requerido)",
                useSampleAvatarLink: "Usar un avatar de ejemplo",
                selectSampleAvatarLabel: "Seleccione un avatar de ejemplo",

                fullNameLabel:
                    "Nombre completo de la persona fallecida (requerido)",
                fullNamePlaceholder: "Juan Pérez",

                preferredNameLabel:
                    "Nombre preferido de la persona fallecida (un nombre o apodo)",
                preferredNamePlaceholder: "Juan",

                birthDateLabel: "Fecha de nacimiento de la persona fallecida",
                deathDateLabel: "Fecha de fallecimiento de la persona fallecida",

                hidePreplanningCheckboxLabel:
                    "Ocultar la casilla de planificación previa",
                defaultPreplanningCheckboxLabel:
                    "Marcar la casilla de planificación previa por defecto",
                preplanningLabel: "Etiqueta de la casilla de planificación previa",
                preplanningLabelPlaceholder:
                    "Me gustaría información sobre la planificación funeraria",

                mobileLabelHeaderLabel: "Pregunta de Planificación Previa - Encabezado de Sección (Móvil)",
                mobileLabelHeaderPlaceholder:
                    "Interesado en planificar con anticipación",

                mobileQuestionLabel: "Pregunta de Planificación Previa - Texto (Móvil)",
                mobileQuestionPlaceholder:
                    "Sí, estoy interesado en planificar con anticipación mis propios arreglos.",

                viewAdvancedOptionsLabel: "Ver opciones avanzadas",

                createButton: "Crear Servicio",
                updateButton: "Actualizar Servicio",

                savingCreateMsg:
                    "Su servicio se está creando. Esto tarda unos 10 segundos…",
                savingUpdateMsg:
                    "Su servicio se está actualizando. Esto tarda unos 10 segundos…",
            },
        },

        // iPad Management
        IPads: {
            list: {
                title: "Ver iPads",
                addIPad: "Agregar iPad",
                searchPlaceholder: "Buscar iPads...",
                filterByStatus: "Filtrar por estado",
                allStatuses: "Todos los estados",
                active: "Activo",
                inactive: "Inactivo",
                setupPending: "Configuración pendiente",
                noIPads: "No se encontraron iPads",
                name: "Nombre",
                status: "Estado",
                activeService: "Servicio activo",
                lastActivity: "Última actividad",
                actions: "Acciones",
                view: "Ver",
                edit: "Editar",
                setup: "Configurar",
                deactivate: "Desactivar",
                activate: "Activar",
                delete: "Eliminar",
                confirmDelete: "¿Está seguro de que desea eliminar este iPad?",
                confirmDeactivate: "¿Está seguro de que desea desactivar este iPad?",
                moreActions: "Más acciones",
            },
            add: {
                title: "Agregar iPad",
                subtitle: "Registrar un nuevo dispositivo iPad",
                nameLabel: "Nombre del iPad",
                namePlaceholder: "iPad del vestíbulo principal",
                descriptionLabel: "Descripción",
                descriptionPlaceholder: "Descripción opcional",
                addButton: "Agregar iPad",
                adding: "Agregando iPad...",
                iPadAdded: "iPad agregado exitosamente",
                setupInstructions: "Siga las instrucciones de configuración para completar el registro",
            },
            setup: {
                title: "Configurar iPad",
                subtitle: "Complete el proceso de configuración en su dispositivo iPad",
                step1: "Abra la aplicación Signature Guestbook en el iPad",
                step2: "Toque en 'Configurar dispositivo'",
                step3: "Cuando se le solicite una dirección de correo electrónico, ingrese este código en su lugar:",
                step4: "Toque 'Completar configuración'",
                deviceCode: "Código del dispositivo",
                copyCode: "Copiar código",
                codeCopied: "Código copiado al portapapeles",
                completeSetup: "Completar configuración",
                completing: "Completando configuración...",
                setupComplete: "Configuración del iPad completada exitosamente",
                troubleshooting: "Configurar iPad manualmente",
            },
            manage: {
                title: "Administrar iPad",
                details: "Detalles",
                activity: "Actividad",
                services: "Servicios",
                settings: "Configuración",
                name: "Nombre",
                description: "Descripción",
                status: "Estado",
                deviceId: "ID del dispositivo",
                lastActive: "Última actividad",
                totalServices: "Total de servicios",
                totalSignatures: "Total de firmas",
                currentService: "Servicio actual",
                noCurrentService: "Sin servicio activo",
                sendService: "Enviar servicio",
                endService: "Finalizar servicio",
                update: "Actualizar",
                updating: "Actualizando...",
                updated: "iPad actualizado exitosamente",
            },
        },

        // Account Management
        Account: {
            details: {
                title: "Detalles de la cuenta",
                accountInfo: "Información de la cuenta",
                businessName: "Nombre de Funeraria / Cementerio",
                contactName: "Nombre de contacto",
                email: "Correo electrónico",
                phone: "Teléfono",
                address: "Dirección",
                logo: "Logo",
                uploadLogo: "Subir logo",
                removeLogo: "Eliminar logo",
                update: "Actualizar",
                updating: "Actualizando...",
                updated: "Cuenta actualizada exitosamente",
            },
            billing: {
                title: "Facturación y suscripción",
                currentPlan: "Plan actual",
                planDetails: "Detalles del plan",
                signatures: "Firmas",
                users: "Usuarios",
                ipads: "iPads",
                price: "Precio",
                billingCycle: "Ciclo de facturación",
                nextBilling: "Próxima fecha de facturación",
                paymentMethod: "Método de pago",
                updatePayment: "Actualizar método de pago",
                changePlan: "Cambiar plan",
                choosePlan: "Elija su plan",
                subscribe: "Suscribirse",
                confirmPlanChange: "Confirmar cambio de plan",
                cancelSubscription: "Cancelar suscripción",
                invoices: "Facturas",
                date: "Fecha",
                amount: "Monto",
                status: "Estado",
                download: "Descargar",
                noInvoices: "No se encontraron facturas",
                banner: {
                    freeTrialExpiring: "Su prueba gratuita expira en {days} días. Actualice ahora para continuar usando todas las funciones.",
                    freeTrialExpiringSoon: "Su prueba gratuita expira pronto. Actualice ahora para evitar la interrupción del servicio.",
                    freeTrialExpiredToday: "Su prueba gratuita expira hoy. Actualice ahora para continuar el servicio.",
                    freeTrialExpired: "Su prueba gratuita ha expirado. Actualice ahora para restaurar el acceso completo.",
                    upgradeNow: "Actualizar Ahora",
                    daysRemaining: "{days} días restantes",
                    dayRemaining: "1 día restante",
                },
            },
            users: {
                title: "Administrar usuarios",
                addUser: "Agregar usuario",
                searchPlaceholder: "Buscar usuarios...",
                filterByRole: "Filtrar por rol",
                allRoles: "Todos los roles",
                admin: "Administrador",
                user: "Usuario",
                noUsers: "No se encontraron usuarios",
                name: "Nombre",
                email: "Correo electrónico",
                role: "Rol",
                status: "Estado",
                lastActive: "Última actividad",
                actions: "Acciones",
                edit: "Editar",
                remove: "Eliminar",
                confirmRemove: "¿Está seguro de que desea eliminar este usuario?",
                invitePending: "Invitación pendiente",
                resendInvite: "Reenviar invitación",
                cancelInvite: "Cancelar invitación",
            },
            addUser: {
                title: "Agregar usuario",
                subtitle: "Invitar a un nuevo usuario a la cuenta",
                emailLabel: "Correo electrónico",
                emailPlaceholder: "usuario@ejemplo.com",
                roleLabel: "Rol",
                sendInvite: "Enviar invitación",
                sending: "Enviando invitación...",
                inviteSent: "Invitación enviada exitosamente",
                userExists: "El usuario ya existe",
            },
        },

        // Public Pages
        Public: {
            sign: {
                title: "Firmar libro de visitas",
                subtitle: "Deje su mensaje",
                nameLabel: "Su nombre",
                namePlaceholder: "Ingrese su nombre",
                messageLabel: "Su mensaje",
                messagePlaceholder: "Comparta sus pensamientos y recuerdos...",
                signMethod: "Método de firma",
                drawSignature: "Dibujar firma",
                typeSignature: "Escribir firma",
                clearSignature: "Limpiar",
                submitSignature: "Enviar",
                submitting: "Enviando...",
                signatureSubmitted: "Firma enviada exitosamente",
                thankYou: "Gracias por firmar",
                thankYouMessage: "¡Gracias por firmar el libro de visitas!",
                returnMessage: "Ahora puede cerrar esta página",
                requiredName: "El nombre es requerido",
                requiredSignature: "La firma es requerida",
                drawPrompt: "Firme en el cuadro de arriba",
                preplanningOption: "Me gustaría información sobre planificación funeraria",
                emailLabel: "Correo (opcional)",
                emailPlaceholder: "su@correo.com",
                phoneLabel: "Teléfono (opcional)",
                phonePlaceholder: "(555) 123-4567",
                mailingAddressLabel: "Dirección postal (opcional)",
                mailingAddressLine1Placeholder: "Línea 1, ej. 123 Calle Principal",
                mailingAddressLine2Placeholder: "Línea 2, ej. Indianapolis, IN 46202",
                signGuestbookLegend: "Firmar el libro de visitas:",
                signing: "Firmando...",
                signGuestbook: "Firmar libro de visitas",
                interestedInPlanningLabel: "Interesado en planificar con anticipación",
                interestedInPlanningText: "Sí, estoy interesado en planificar mis propios arreglos.",
            },
            family: {
                title: "Libro de visitas familiar",
                subtitle: "Ver mensajes de amigos y familiares",
                forFamily: "Para la familia de",
                viewSignatures: "Ver todas las firmas",
                totalSignatures: "Total de firmas",
                shareGuestbook: "Compartir libro de visitas",
                downloadPDF: "Descargar PDF",
                searchSignatures: "Buscar firmas...",
                noSignaturesYet: "Aún no hay firmas",
                firstToSign: "Sea el primero en firmar",
                signGuestbook: "Firmar libro de visitas",
                name: "Nombre",
                message: "Mensaje",
                signedOn: "Firmado el",
                showMore: "Mostrar más",
                showLess: "Mostrar menos",
                loading: "Cargando...",
                virtualSignatures: "Firmas virtuales:",
                nameNotAvailable: "(Nombre no disponible)",
                phoneNone: "(ninguno)",
                emailNone: "(ninguno)",
                waitingForTranscription: "(Esperando transcripción de firma)",
                thankYouMessage: "¡Gracias por firmar el libro de visitas!",
            },
        },

        // Error Messages
        Errors: {
            generic: "Ocurrió un error",
            network: "Error de red. Por favor verifique su conexión",
            server: "Error del servidor. Por favor intente más tarde",
            notFound: "Página no encontrada",
            unauthorized: "Acceso no autorizado",
            forbidden: "Acceso prohibido",
            validation: "Error de validación",
            required: "Este campo es requerido",
            invalidFormat: "Formato inválido",
            tooShort: "Demasiado corto",
            tooLong: "Demasiado largo",
            passwordWeak: "La contraseña es muy débil",
            emailInvalid: "Correo electrónico inválido",
            phoneInvalid: "Número de teléfono inválido",
            dateInvalid: "Fecha inválida",
            fileTooBig: "El archivo es demasiado grande",
            fileTypeInvalid: "Tipo de archivo inválido",
            uploadFailed: "Error al subir",
            downloadFailed: "Error al descargar",
            timeout: "Tiempo de espera agotado",
            offline: "Parece que está sin conexión",
            maintenance: "Sistema en mantenimiento",
        },

        // Success Messages
        Success: {
            saved: "Guardado exitosamente",
            updated: "Actualizado exitosamente",
            deleted: "Eliminado exitosamente",
            created: "Creado exitosamente",
            sent: "Enviado exitosamente",
            uploaded: "Subido exitosamente",
            downloaded: "Descargado exitosamente",
            copied: "Copiado al portapapeles",
            shared: "Compartido exitosamente",
            completed: "Completado exitosamente",
            activated: "Activado exitosamente",
            deactivated: "Desactivado exitosamente",
            approved: "Aprobado exitosamente",
            rejected: "Rechazado exitosamente",
            subscribed: "Suscrito exitosamente",
            unsubscribed: "Desuscrito exitosamente",
        },

        // Form Validation
        Validation: {
            required: "{field} es requerido",
            email: "Por favor ingrese un correo electrónico válido",
            phone: "Por favor ingrese un número de teléfono válido",
            password: "La contraseña debe tener al menos 8 caracteres",
            passwordMatch: "Las contraseñas no coinciden",
            minLength: "Debe tener al menos {min} caracteres",
            maxLength: "No debe tener más de {max} caracteres",
            minValue: "Debe ser al menos {min}",
            maxValue: "No debe ser más de {max}",
            alphanumeric: "Debe contener solo letras y números",
            numeric: "Debe contener solo números",
            alphabetic: "Debe contener solo letras",
            url: "Por favor ingrese una URL válida",
            date: "Por favor ingrese una fecha válida",
            time: "Por favor ingrese una hora válida",
            fileSize: "El archivo debe ser menor a {size}",
            fileType: "El archivo debe ser {types}",
            unique: "Este valor ya está en uso",
            agreement: "Debe aceptar para continuar",
        },

        // Modal Dialogs
        Modals: {
            confirm: {
                title: "Confirmar acción",
                message: "¿Está seguro de que desea continuar?",
                confirmButton: "Confirmar",
                cancelButton: "Cancelar",
            },
            delete: {
                title: "Confirmación de eliminación",
                message: "¿Está seguro de que desea eliminar esto?",
                warning: "Esta acción no se puede deshacer.",
                confirmButton: "Eliminar",
                cancelButton: "Cancelar",
            },
            unsavedChanges: {
                title: "Cambios sin guardar",
                message: "Tiene cambios sin guardar. ¿Qué desea hacer?",
                saveButton: "Guardar cambios",
                discardButton: "Descartar cambios",
                cancelButton: "Cancelar",
            },
            error: {
                title: "Error",
                message: "Ocurrió un error al procesar su solicitud.",
                tryAgainButton: "Intentar de nuevo",
                closeButton: "Cerrar",
            },
            success: {
                title: "Éxito",
                message: "Operación completada exitosamente.",
                continueButton: "Continuar",
            },
        },

        // Date/Time
        DateTime: {
            today: "Hoy",
            yesterday: "Ayer",
            tomorrow: "Mañana",
            thisWeek: "Esta semana",
            lastWeek: "Semana pasada",
            nextWeek: "Próxima semana",
            thisMonth: "Este mes",
            lastMonth: "Mes pasado",
            nextMonth: "Próximo mes",
            thisYear: "Este año",
            lastYear: "Año pasado",
            nextYear: "Próximo año",
            days: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
            daysShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
            months: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
            monthsShort: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
            am: "AM",
            pm: "PM",
            dateFormat: "DD/MM/AAAA",
            timeFormat: "h:mm A",
            dateTimeFormat: "DD/MM/AAAA h:mm A",
        },

        // Pagination
        Pagination: {
            showing: "Mostrando",
            of: "de",
            entries: "entradas",
            page: "Página",
            pages: "páginas",
            previous: "Anterior",
            next: "Siguiente",
            first: "Primera",
            last: "Última",
            goToPage: "Ir a la página",
            rowsPerPage: "Filas por página",
        },

        // Empty States
        EmptyStates: {
            noServices: "No se encontraron servicios. Cree su primer servicio para comenzar.",
            noSignatures: "Aún no hay firmas. Comparta el código QR o enlace para comenzar a recolectar firmas.",
            noIPads: "No hay iPads configurados. Agregue un iPad para habilitar la recolección de firmas en persona.",
            noUsers: "No se encontraron usuarios. Invite a miembros del equipo para colaborar.",
            noInvoices: "No hay facturas disponibles.",
            noResults: "No se encontraron resultados para su búsqueda.",
            noData: "No hay datos disponibles.",
            createFirst: "Cree su primer {item} para comenzar.",
            inviteFirst: "Invite a su primer {item} para comenzar.",
            connectFirst: "Conecte su primer {item} para comenzar.",
            searchDifferent: "Intente buscar algo diferente.",
            clearFilters: "Limpie los filtros para ver todos los resultados.",
        },

        // Layout Errors
        Layout: {
            serviceRequired: "Se requiere servicio.",
            unableToLoadService: "No se puede cargar el servicio",
            retry: "Reintentar",
        },

        // Help/Tooltips
        Help: {
            servicePhoto: "Suba una foto del fallecido. Aparecerá en la página de firma y en el libro de visitas.",
            preferredName: "Ingrese un apodo o nombre preferido. Se usará en saludos y mensajes.",
            preplanningOption: "Active esto para recolectar información de contacto de invitados interesados en servicios de planificación previa.",
            iPadSetup: "Siga las instrucciones de configuración en el iPad para completar el registro del dispositivo.",
            familyAccess: "Comparta este enlace con familiares para ver y descargar el libro de visitas.",
            signatureTypes: "Los invitados pueden dibujar su firma o escribir su nombre.",
            exportOptions: "Exporte firmas como PDF para imprimir o CSV para análisis de datos.",
            userRoles: "Los administradores pueden gestionar todos los aspectos de la cuenta. Los usuarios pueden crear y gestionar servicios.",
            billing: "Su suscripción se renovará automáticamente cada período de facturación.",
            support: "¿Necesita ayuda? Contacte a nuestro equipo de soporte en support@signatureguestbook.com",
        },

        // Management section for agency admins and super users
        Management: {
            title: "Gestión",
            viewAccounts: "Ver Cuentas",
            viewAllAccounts: "Ver Todas las Cuentas",
            addAccount: "Agregar Cuenta",
            viewUsers: "Ver Usuarios",
        },

        // Admin section for super users
        Admin: {
            title: "Administración",
            viewAgencies: "Ver Agencias",
            addAgency: "Agregar Agencia",
            manageCrops: "Gestionar Recortes",
            generatedPDFs: "PDFs Generados",
            systemSettings: "Configuración del Sistema",
        },

        // Books section
        Books: {
            title: "Libros",
            viewBooks: "Ver Libros",
            createBook: "Crear Libro",
        },
        
        // Follow-up Messages section
        FollowUpMessages: {
            title: "Mensajes de Seguimiento",
            subtitle: "Envíe mensajes de agradecimiento personalizados a los asistentes del servicio",
            enable: {
                title: "Habilitar Mensajes de Seguimiento",
                description: "Los mensajes de seguimiento no están habilitados para esta cuenta. Configure su dirección de correo electrónico a continuación para comenzar a enviar mensajes de agradecimiento.",
                emailLabel: "Su Dirección de Correo Electrónico",
                emailDescription: "Esta será su dirección única de respuesta para todas las campañas de correo electrónico.",
                enableButton: "Habilitar Mensajes de Seguimiento",
                enabling: "Habilitando...",
                helpText: "Esta función le ayuda a enviar mensajes de agradecimiento personalizados a los invitados que firman el libro de visitas digital.",
            },
            tabs: {
                reviewQueue: "Cola de Revisión",
                createMessage: "Crear Mensaje",
                history: "Historial",
                analytics: "Análisis",
                settings: "Configuración",
            },
            training: {
                modeBanner: "Modo de Entrenamiento:",
                progress: "Progreso:",
                messagesRemaining: "mensajes más necesitan ser aprobados antes de que comiencen las sugerencias automáticas.",
                messageRemaining: "mensaje más necesita ser aprobado antes de que comiencen las sugerencias automáticas.",
            },
        },
    },
};

/** ------------------------------------------------------------------ */
/** 3️⃣  Hook implementation.                                          */
/** ------------------------------------------------------------------ */
function useLocalizedCopy() {
    const [language, setLanguage] = useState<Language>(Language.EN_US);

    useEffect(() => {
        const stored = getCurrentLanguage() as Language | null;
        if (stored && Object.values(Language).includes(stored)) {
            setLanguage(stored);
        }
    }, []);

    // Always fall back to EN_US if something is missing
    const copy = localizedCopy[language] ?? localizedCopy[Language.EN_US];
    return { copy };
}

export default useLocalizedCopy;